import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

export interface CardStepController {
  registered: boolean;
  index: number;
  total: number;
  nextDisabled: boolean;
  consumeNext: () => boolean;
  consumePrev: () => boolean;
}

const idleController: CardStepController = {
  registered: false,
  index: 0,
  total: 0,
  nextDisabled: false,
  consumeNext: () => false,
  consumePrev: () => false,
};

interface CardNavDispatch {
  setController: (controller: Omit<CardStepController, "registered"> | null) => void;
}

const CardNavStateContext = createContext<CardStepController>(idleController);
const CardNavDispatchContext = createContext<CardNavDispatch>({ setController: () => {} });

export function CardNavProvider({ children }: { children: React.ReactNode }) {
  const [controller, setControllerState] = useState<CardStepController>(idleController);

  const setController = useCallback((next: Omit<CardStepController, "registered"> | null) => {
    if (!next) {
      setControllerState(idleController);
      return;
    }
    setControllerState({ ...next, registered: true });
  }, []);

  const dispatch = useMemo(() => ({ setController }), [setController]);

  return (
    <CardNavDispatchContext.Provider value={dispatch}>
      <CardNavStateContext.Provider value={controller}>
        {children}
      </CardNavStateContext.Provider>
    </CardNavDispatchContext.Provider>
  );
}

export function useCardSteps(): CardStepController {
  return useContext(CardNavStateContext);
}

export function useRegisterCardSteps(
  enabled: boolean,
  index: number,
  total: number,
  nextDisabled: boolean,
  consumeNext: () => boolean,
  consumePrev: () => boolean
) {
  const { setController } = useContext(CardNavDispatchContext);
  const nextRef = useRef(consumeNext);
  const prevRef = useRef(consumePrev);
  nextRef.current = consumeNext;
  prevRef.current = consumePrev;

  useEffect(() => {
    if (!enabled || total < 1) {
      setController(null);
      return;
    }
    setController({
      index,
      total,
      nextDisabled,
      consumeNext: () => nextRef.current(),
      consumePrev: () => prevRef.current(),
    });
  }, [enabled, index, total, nextDisabled, setController]);

  // Unmount only. Clearing on every dep change made CardShell remount the
  // scene (sceneKey flipped registered→false), which re-registered forever.
  useEffect(() => {
    return () => setController(null);
  }, [setController]);
}
